import React, { useMemo, useState } from 'react';
import { Box, Divider, Grid, Typography } from '@material-ui/core';
import { Image } from 'react-feather';
import { Theme, useTheme } from '@material-ui/core/styles';
import numeral from 'numeral';
import { useTranslation } from 'react-i18next';
import { useConversationMediaGallery } from 'src/views/OmniChannel/hooks/useConversationMediaGallery';
import { MEDIA_GALLERY_PREVIEW_LIMIT } from 'src/views/OmniChannel/constants/mediaGallery';
import { MediaGallerySkeleton } from './MediaGallerySkeleton';

export const ChatMedia = ({
    conversationId,
    onClick,
    enabled = true,
}: {
    conversationId: string;
    onClick: () => void;
    enabled?: boolean;
}) => {
    const theme = useTheme<{
        palette: Theme['palette'] & { background: { dark: string } };
    }>();
    const { t } = useTranslation();
    const [hover, setHover] = useState<boolean>(false);

    const { groupedMedia, galleryTotal, galleryLoading } =
        useConversationMediaGallery({
            conversationId,
            tab: 'all',
            enabled: enabled && Boolean(conversationId),
            mode: 'local',
        });

    const previewImages = useMemo(
        () => groupedMedia.images.slice(0, MEDIA_GALLERY_PREVIEW_LIMIT),
        [groupedMedia.images]
    );

    const totalMediaCount = galleryTotal ?? groupedMedia.all.length;

    const hoveredStyle = {
        backgroundColor: theme.palette.action.hover,
    };

    return (
        <Box
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
            }}
        >
            <Typography variant="h4" color="textPrimary">
                Info.
            </Typography>
            <Divider style={{ marginTop: 10, marginBottom: 10 }} />
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    {galleryLoading ? (
                        <Box style={{ padding: '10px 0' }}>
                            <MediaGallerySkeleton variant="list" />
                        </Box>
                    ) : totalMediaCount === 0 ? (
                        <Box
                            style={{
                                padding: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 10,
                            }}
                        >
                            <Image
                                size={20}
                                color={theme.palette.text.primary}
                            />
                            <Typography variant="body1" color="textPrimary">
                                {t('Chat.MediaGalleryEmpty')}
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            style={{
                                padding: '30px 20px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                cursor: 'pointer',
                                transition: '0.4s',
                                borderRadius: 12,
                                ...(hover ? hoveredStyle : {}),
                            }}
                            onMouseEnter={() => setHover(true)}
                            onMouseLeave={() => setHover(false)}
                            onClick={onClick}
                        >
                            <Box
                                style={{
                                    display: 'flex',
                                    gap: 10,
                                    flexGrow: 1,
                                    alignItems: 'center',
                                }}
                            >
                                <Image
                                    size={20}
                                    color={theme.palette.text.primary}
                                />
                                <Typography
                                    variant="body1"
                                    color="textPrimary"
                                    style={{ fontSize: 20 }}
                                >
                                    {t('Chat.MediaGallerySharedFiles')}
                                </Typography>
                            </Box>
                            <Typography variant="body1" color="textPrimary">
                                {numeral(totalMediaCount).format('0,0')}
                            </Typography>
                        </Box>
                    )}
                </Grid>
                {previewImages.map((item) => (
                    <Grid item xs={3} key={item.id}>
                        <img
                            src={item.source}
                            alt={item.name}
                            onClick={onClick}
                            style={{
                                width: '100%',
                                height: 120,
                                objectFit: 'cover',
                                borderRadius: 5,
                                cursor: 'pointer',
                            }}
                        />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};
